"use client";

import PopularPodcastItem from "../popular-podcast-item";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { ArrowRightIcon } from "lucide-react";
import { useI18n } from "@/lib/i18n";

interface Podcast {
  ownerAvatarUrl: string;
  author: string;
  name: string;
  slug?: string;
  defaultLevelRange?: string[] | string | null;
  default_level_range?: string[] | string | null;
}

interface PopularChannelsSectionProps {
  podcasts: Podcast[];
  isLoading?: boolean;
}

const normalizeLevelRange = (value: Podcast["defaultLevelRange"]): string[] => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.map(String).map(item => item.trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map(String).map(item => item.trim()).filter(Boolean);
      }
    } catch {
      // Ignore JSON parse errors and fall back to string splitting
    }

    return value
      .split(/[-,|]/)
      .map(part => part.trim())
      .filter(Boolean);
  }

  return [];
};

export default function PopularChannelsSection({ podcasts, isLoading }: PopularChannelsSectionProps) {
  const { t } = useI18n();
  if (isLoading) {
    return (
      <section>
        <header className="pl-2 pb-1 sm:pb-4 flex justify-between items-center">
          <h2 className="font-sans text-xl sm:text-2xl font-bold">{t('home.channels.title')}</h2>
          <Button className="h-auto py-1 text-sm sm:text-normal font-normal" variant="link" asChild>
            <Link href="/channels">
              <span className="text-xs sm:text-normal font-normal">{t('home.channels.viewAll')}</span> <ArrowRightIcon className="size-3 ml-1" />
            </Link>
          </Button>
        </header>
        
        <div className="w-full overflow-x-auto scrollbar-hide max-w-[calc(100vw-2rem)] sm:max-w-[calc(100vw-2rem)]">
          <div className="flex gap-2" style={{ width: 'fit-content' }}>
            {/* Loading skeleton */}
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="flex-shrink-0 w-48">
                <div className="animate-pulse">
                  <div className="w-12 h-12 bg-muted rounded-full mb-3"></div>
                  <div className="bg-muted h-4 rounded mb-2"></div>
                  <div className="bg-muted h-3 rounded w-2/3"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const normalizedPodcasts = podcasts.map((podcast) => ({
    ...podcast,
    defaultLevelRange: normalizeLevelRange(podcast.defaultLevelRange ?? podcast.default_level_range ?? null),
  }));

  return (
    <section>
      <header className="pl-2 pb-1 sm:pb-4 flex justify-between items-center">
        <h2 className="font-sans text-xl sm:text-2xl font-bold">{t('home.channels.title')}</h2>
        <Button className="h-auto py-1 text-sm sm:text-normal font-normal" variant="link" asChild>
          <Link href="/channels">
            <span className="text-xs sm:text-normal font-normal">{t('home.channels.viewAll')}</span> <ArrowRightIcon className="size-3 ml-1" />
          </Link>
        </Button>
      </header>
      
      <div className="w-full overflow-x-auto scrollbar-hide max-w-[calc(100vw-2rem)] sm:max-w-[calc(100vw-2rem)]">
        <div className="flex gap-2" style={{ width: 'fit-content' }}>
          {normalizedPodcasts.slice(0, 100).map((podcast, index) => (
            <div
              key={`${podcast.name || podcast.author || 'channel'}-${index}`}
              className="flex-shrink-0"
            >
              <PopularPodcastItem podcast={podcast} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
